import * as React from "react";
import {
  Accordion, AccordionButton, AccordionIcon,
  AccordionItem, AccordionPanel,
  Box,
  Flex, Grid, GridItem, Heading,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from "@chakra-ui/react";
import CategoryPreviewWrapper from "./CategoryPreviewWrapper";
import { useTranslation } from "react-i18next";
import { translateSubElementString } from "../../../../../../../utils";
import { renderNoData } from "../helpers";
import NumberField from "../../../../../../../components/NumberField/NumberField";
import { POWER_SOURCES } from "../../../../../../OrganizationQuestionnaire/helpers";
import { isBiogenic } from "utils/carbon-utils";

function FuelAndEnergyRelatedActivitiesPreview({ currentElement }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "reportsPage.scopeThree.inputs.fuelAndEnergyRelatedActivities"
  });

  const fuelItems = currentElement?.upstreamEmissionsOfPurchasedFuels;
  const fuelItemsNormal = fuelItems.filter(item => ! isBiogenic(item.consumptionType));
  const fuelItemsBiogenic = fuelItems.filter(item => isBiogenic(item.consumptionType));

  return (
    <CategoryPreviewWrapper>

      <Flex marginBlockEnd={4}>
        <Text size="sm" fontWeight="bold">
          {translateSubElementString(
            `reportsPage.scopeThree.inputs.mainTotalEmissions`
          )}
        </Text>
        <NumberField value={currentElement?.fuelAndEnergyRelatedActivitiesTotal} marginStart="2" />
      </Flex>

      <Accordion allowMultiple marginBottom="10">
        <AccordionItem>
          <AccordionButton>
            <Box flex="1" textAlign="left">
              <Heading color="esg_gray.200" size="xs">
                <Text fontSize="md" fontWeight="bold">{"A - Upstream emissions of purchased fuels"}</Text>
              </Heading>
            </Box>
            <AccordionIcon />
          </AccordionButton>
            <AccordionPanel pb={4}>

            {/* PURCHASED FUEL 1*/}
          <TableContainer>
            <Table variant="simple" size="lg">
              <Thead>
                <Tr>
                  <Th textTransform="none">{t("consumptionType")}</Th>
                  <Th textTransform="none">
                    {translateSubElementString("reportsPage.scopeThree.inputs.fuelAndEnergyRelatedActivities.emissions")}
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {fuelItems?.length > 0 ?
                  [...fuelItemsNormal.map(item => {
                    return (
                      <Tr key={`${item.consumptionType.id}`}>
                        <Td>{POWER_SOURCES[item.consumptionType.code] || item.consumptionType.name || item.consumptionType.code}</Td>
                        <Td><NumberField value={item.emissions} align={"left"} /></Td>
                      </Tr>
                    );
                  }),
				  fuelItemsBiogenic.length > 0 ? <Tr key='biogenic-separator'>
					<Td colSpan={2}><i>Biogenic items (CO<sub>2</sub> not included in emissions)</i></Td>
				  </Tr> : null,
				  ...fuelItemsBiogenic.map(item => {
					return (
					  <Tr key={`${item.consumptionType.id}`}>
						<Td>{POWER_SOURCES[item.consumptionType.code] || item.consumptionType.name || item.consumptionType.code}</Td>
						<Td><NumberField value={item.emissions} align={"left"} /></Td>
					  </Tr>
					);
				  })
				]
				  :
                  renderNoData(2)
                }
              </Tbody>
            </Table>
          </TableContainer>
            </AccordionPanel>
        </AccordionItem>
        <AccordionItem>
          <AccordionButton>
            <Box flex="1" textAlign="left">
              <Heading color="esg_gray.200" size="xs">
                <Text fontSize="md" fontWeight="bold">{"B - " + t("upstreamEmissionsOfPurchasedElectricity")}</Text>
              </Heading>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel pb={4}>
      {/* PURCHASED FUEL 2*/}

          <TableContainer>
            <Table variant="simple" size="lg">
              <Thead>
                <Tr>
                  <Th textTransform="none">{t("country")}</Th>
                  <Th textTransform="none">
                    {translateSubElementString("reportsPage.scopeThree.inputs.fuelAndEnergyRelatedActivities.emissions")}
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {currentElement?.purchasedElectricityUpstreamEmissions?.length > 0 ?
                  currentElement.purchasedElectricityUpstreamEmissions.map(item => {
                    return (
                      <Tr key={`${item.id}-${item.country}-electricity`}>
                        <Td>{item.country}</Td>
                        <Td><NumberField value={item.emissions} align={"left"} /></Td>
                      </Tr>
                    );
                  }) :
                  renderNoData(2)
                }
              </Tbody>
            </Table>
          </TableContainer>
          </AccordionPanel>
        </AccordionItem>
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <Heading color="esg_gray.200" size="xs">
                    <Text fontSize="md" fontWeight="bold">{"C - " + t("emissionsFromTransmissionAndDistributionLosses")}</Text>
                  </Heading>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
      {/* TRANSMISSION AND DISTRIBUTION*/}
          <TableContainer>
            <Table variant="simple" size="lg">
              <Thead>
                <Tr>
                  <Th textTransform="none">{t("country")}</Th>
                  <Th textTransform="none">
                    {translateSubElementString("reportsPage.scopeThree.inputs.fuelAndEnergyRelatedActivities.emissions")}
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {currentElement?.transmissionAndDistributionLossesEmissions?.length > 0 ?
                  currentElement.transmissionAndDistributionLossesEmissions.map(item => {
                    return (
                      <Tr key={`${item.id}-${item.country}-losses`}>
                        <Td>{item.country}</Td>
                        <Td><NumberField value={item.emissions} align={"left"} /></Td>
                      </Tr>
                    );
                  }) :
                  renderNoData(2)
                }
              </Tbody>
            </Table>
          </TableContainer>
              </AccordionPanel>
            </AccordionItem>
      </Accordion>
      <Grid templateColumns="repeat(2, 1fr)" gap={2} overflow="auto">
        <GridItem>
          <Heading size="sm" marginBlock="1" align="left">
            {t("activity")}
          </Heading>
        </GridItem>
        <GridItem>
          <Heading size="sm" marginBlock="1" align="right">
            {translateSubElementString("reportsPage.totalEmissions")}
          </Heading>
        </GridItem>
        <GridItem>
          <Box inlineSize="400px">
            <Text
              display="flex"
              justifyContent="start"
              alignItems="center"
              fontSize="sm"
              whiteSpace="break-spaces"
              marginLeft={6}
            >
              Upstream emissions of purchased fuels
            </Text>
          </Box>
        </GridItem>
        <GridItem>
          <NumberField value={currentElement?.upstreamEmissionsOfPurchasedFuelsTotal} />
        </GridItem>
        <GridItem>
          <Box inlineSize="400px">
            <Text
              display="flex"
              justifyContent="start"
              alignItems="center"
              fontSize="sm"
              whiteSpace="break-spaces"
              marginLeft={6}
            >
              {t("upstreamEmissionsOfPurchasedElectricity")}
            </Text>
          </Box>
        </GridItem>
        <GridItem>
          <NumberField value={currentElement?.purchasedElectricityUpstreamEmissionsTotal} />
        </GridItem>
        <GridItem>
          <Box inlineSize="400px">
            <Text
              display="flex"
              justifyContent="start"
              alignItems="center"
              fontSize="sm"
              whiteSpace="break-spaces"
              marginLeft={6}
            >
              {t("emissionsFromTransmissionAndDistributionLosses")}
            </Text>
          </Box>
        </GridItem>
        <GridItem>
          <NumberField value={currentElement?.transmissionAndDistributionLossesEmissionsTotal} />
        </GridItem>
      </Grid>
    </CategoryPreviewWrapper>
  );
}

export default FuelAndEnergyRelatedActivitiesPreview;